import { Component, HostBinding, Input, ViewEncapsulation } from '@angular/core';
import { AbstractComponent } from '@sol/components/components/abstract.component';
import { NgStyle } from '@angular/common';
import { TimerTools } from '@sol/components/tools/timer-tools';
import { SkeletonTools } from '@sol/components/tools/skeleton-tools';
import { SolLoader } from '@sol/components';
import { SolLoaderState } from './lab-loader.state';
import { Subscription } from 'rxjs';

@Component({
  selector: 'lab-loader',
  standalone: true,
  imports: [NgStyle],
  templateUrl: './lab-loader.component.html',
  styleUrl: './lab-loader.component.scss',
  encapsulation: ViewEncapsulation.None,
  host: { role: 'status', 'aria-busy': 'true', 'aria-label': 'Loading' },
})
export class LabLoaderComponent extends AbstractComponent<SolLoaderState> {
  // Computed
  protected readonly hostClass$ = this.compute(state => state.hostClass);
  protected readonly rows$ = this.compute(state => state.renderRows);
  protected readonly loading$ = this.compute(state => state.loading);

  // Cache
  private waveSubscription?: Subscription;

  constructor() {
    super(SolLoaderState.of(SolLoader.ofTable()));

    this.watchComputed(
      state => ({ loading: state.loading, frozen: state.loader.frozen }),
      data => this.toggleWaveInterval(data.loading && !data.frozen),
    );
  }

  @HostBinding('class')
  public get hostClass(): string {
    return this.hostClass$();
  }

  @Input()
  public set loader(loader: SolLoader) {
    this.update(state => state.withLoader(loader));
  }

  @Input()
  public set loading(loading: boolean) {
    this.update(state => state.withLoading(loading));
  }

  private toggleWaveInterval(animate: boolean): void {
    // Stop animation
    if (!animate) {
      this.waveSubscription?.unsubscribe();
      this.waveSubscription = undefined;
      return;
    }

    // Already animating
    if (this.waveSubscription !== undefined) {
      return;
    }

    // Start animation
    this.waveSubscription = TimerTools.interval$(SkeletonTools.intervalMs, this.destroyRef).subscribe(() => {
      requestAnimationFrame(() => this.tickWave());
    });
  }

  private tickWave() {
    this.update(state => state.withWaveIndex(SkeletonTools.bumpWaveIndex(state.waveIndex)));
  }
}
